import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AppointmentTypes, AvailabilityTypes, LocationTypes, CourseTypes } from 'common';
import { BookingWizardFlowTypes } from 'common/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import { LessonAppointmentAvailability } from 'common/infrastructure/modules/appointment/interfaces/AvailabilityTypes';

export type BookingFormAddon = {
  name: string;
  sessionTypeId: number;
  cost?: number;
};

export interface BookingForm {
  location?: string | undefined;
  course?: string | undefined;
  locationDetails?: LocationTypes.SimulatorLocation | undefined;
  courseDetails?: CourseTypes.Course | undefined;
  requestTeeTimeDetails?: CourseTypes.CourseRequest | undefined;
  experienceType?: BookingWizardFlowTypes | undefined;
  reservationDate?: string;
  partySize?: string;
  startTime?: string;
  endTime?: string;
  sessionLength?: string;
  sessionTypeId?: number;
  instructor?: string;
  email?: string;
  lessonType?: number;
  bookingType?: string;
  appointments?: AppointmentTypes.Appointment[];
  resourceId?: number;
  instructorDetails?: Partial<LessonAppointmentAvailability>;
  confirmedBooking?: AppointmentTypes.BookedAppointmentResponse[];
  leftHanded?: boolean;
  clubRental?: boolean;
  multiSportSimRental?: boolean;
  bookingUUID?: string;
  addOns?: BookingFormAddon[];
  clubFittingType?: AvailabilityTypes.ClubFittingType;
  totalPrice?: number;
  redirectTo?: string;
  squareOrder?: AppointmentTypes.FoodOrder[];
  irclickid?: string;
  activeInstructorTab?: string;
  // Regional booking tracking
  regionalBookingOriginalLocationId?: string;
  regionalBookingOriginalLocationName?: string;
  regionalBookingSwitchedToAlternative?: boolean;
}

type ReservationDateValue = Date | string | undefined | null;

const normalizeReservationDate = (reservationDate: ReservationDateValue): string | undefined => {
  if (reservationDate == null || reservationDate === '' || reservationDate === 'Invalid Date') {
    return undefined;
  }

  if (reservationDate instanceof Date) {
    return Number.isNaN(reservationDate.getTime()) ? undefined : reservationDate.toISOString();
  }

  const parsedReservationDate = new Date(reservationDate);
  return Number.isNaN(parsedReservationDate.getTime()) ? undefined : reservationDate;
};

const bookingFormSlice = createSlice({
  name: 'bookingForm',
  initialState: {},
  reducers: {
    setLocation: (state: BookingForm, action: PayloadAction<string>) => ({
      ...state,
      location: action.payload,
    }),
    setCourse: (state: BookingForm, action: PayloadAction<string | undefined>) => ({
      ...state,
      course: action.payload,
    }),
    setLocationDetails: (
      state: BookingForm,
      action: PayloadAction<LocationTypes.SimulatorLocation | undefined>,
    ) => ({
      ...state,
      locationDetails: action.payload,
    }),
    setCourseDetails: (
      state: BookingForm,
      action: PayloadAction<CourseTypes.Course | undefined>,
    ) => ({
      ...state,
      courseDetails: action.payload,
    }),
    setRequestTeeTimeDetails: (
      state: BookingForm,
      action: PayloadAction<CourseTypes.CourseRequestDTO | undefined>,
    ) => ({
      ...state,
      requestTeeTimeDetails: action.payload,
    }),
    setExperienceType: (state: BookingForm, { payload }) => ({
      ...state,
      experienceType: payload.experienceType,
    }),
    setActiveInstructorTab: (state: BookingForm, { payload }) => ({
      ...state,
      activeInstructorTab: payload.activeInstructorTab,
    }),
    setInstructor: (state: BookingForm, { payload }) => ({
      ...state,
      instructor: payload.instructor,
    }),
    setReservationDate: (
      state: BookingForm,
      action: PayloadAction<{ reservationDate?: ReservationDateValue }>,
    ) => ({
      ...state,
      reservationDate: normalizeReservationDate(action.payload.reservationDate),
    }),
    setPartySize: (state: BookingForm, { payload }) => ({
      ...state,
      partySize: payload.partySize,
    }),
    setEmail: (state: BookingForm, { payload }) => ({
      ...state,
      email: payload.email,
    }),
    setStartTime: (state: BookingForm, { payload }) => ({
      ...state,
      startTime: payload.startTime,
    }),
    setLessonType: (state: BookingForm, { payload }) => ({
      ...state,
      lessonType: payload.lessonType,
    }),
    setBookingType: (state: BookingForm, { payload }) => ({
      ...state,
      bookingType: payload.bookingType,
    }),
    setStartTimeAndSessionLength: (state: BookingForm, { payload }) => ({
      ...state,
      startTime: payload.startTime,
      endTime: payload.endTime,
      sessionLength: payload.sessionLength,
    }),
    setAppointments: (state: BookingForm, { payload }) => ({
      ...state,
      appointments: payload.appointments,
    }),
    setResourceId: (state: BookingForm, { payload }) => ({
      ...state,
      resourceId: payload.resourceId,
    }),
    setInstructorDetails: (state: BookingForm, { payload }) => ({
      ...state,
      instructorDetails: payload,
    }),
    setBookingWizardData: (state: BookingForm, { payload }) => ({
      ...state,
      ...payload,
    }),
    setConfirmedBooking: (state: BookingForm, { payload }) => ({
      ...state,
      confirmedBooking: payload,
    }),
    setBookingUUID: (state: BookingForm, { payload }) => ({
      ...state,
      bookingUUID: payload.bookingUUID,
    }),
    setLeftHanded: (state: BookingForm, { payload }) => ({
      ...state,
      leftHanded: payload.leftHanded,
    }),
    setClubRental: (state: BookingForm, { payload }) => ({
      ...state,
      clubRental: payload.clubRental,
    }),
    setMultiSportSimRental: (state: BookingForm, { payload }) => ({
      ...state,
      multiSportSimRental: payload.multiSportSimRental,
    }),
    setAddons: (state: BookingForm, { payload }) => ({
      ...state,
      addOns: payload ? [payload] : [],
    }),
    setTotalPrice: (state: BookingForm, { payload }) => ({
      ...state,
      totalPrice: payload,
    }),
    setRedirectTo: (state: BookingForm, { payload }) => ({
      ...state,
      redirectTo: payload,
    }),
    setSquareOrder: (state: BookingForm, action) => ({
      ...state,
      squareOrder: action.payload,
    }),
    setClubFittingType: (state: BookingForm, action) => ({
      ...state,
      clubFittingType: action.payload.clubFittingType,
    }),
    setIrClickId: (state: BookingForm, action: PayloadAction<string | undefined>) => ({
      ...state,
      irclickid: action.payload,
    }),
    // Regional booking tracking actions
    setRegionalBookingOriginalLocation: (
      state: BookingForm,
      action: PayloadAction<{ locationId: string; locationName: string }>
    ) => ({
      ...state,
      regionalBookingOriginalLocationId: action.payload.locationId,
      regionalBookingOriginalLocationName: action.payload.locationName,
    }),
    setRegionalBookingSwitchedToAlternative: (
      state: BookingForm,
      action: PayloadAction<boolean>
    ) => ({
      ...state,
      regionalBookingSwitchedToAlternative: action.payload,
    }),
    clearRegionalBookingTracking: (state: BookingForm) => ({
      ...state,
      regionalBookingOriginalLocationId: undefined,
      regionalBookingOriginalLocationName: undefined,
      regionalBookingSwitchedToAlternative: undefined,
    }),
    clearBookingData: () => ({}),
  },
});

export const {
  setLocation,
  setCourse,
  setCourseDetails,
  setRequestTeeTimeDetails,
  setTotalPrice,
  setExperienceType,
  setActiveInstructorTab,
  setReservationDate,
  setPartySize,
  setInstructor,
  setEmail,
  setStartTimeAndSessionLength,
  setAppointments,
  setLocationDetails,
  setStartTime,
  setResourceId,
  setLessonType,
  setBookingType,
  setInstructorDetails,
  setBookingWizardData,
  setConfirmedBooking,
  setBookingUUID,
  setLeftHanded,
  setClubRental,
  setMultiSportSimRental,
  setAddons,
  setRedirectTo,
  setSquareOrder,
  setClubFittingType,
  setIrClickId,
  setRegionalBookingOriginalLocation,
  setRegionalBookingSwitchedToAlternative,
  clearRegionalBookingTracking,
  clearBookingData,
} = bookingFormSlice.actions;
export default bookingFormSlice.reducer;
